<template>
  <LTextfield
    :id="fieldId"
    :label="alias || ''"
    :disabled="isDisabled"
    :errors="error"
    :infos="infoMessage"
    :input-mode="inputMode as any"
    :type="(type as any) || 'text'"
    :mask="mask"
    :model-value="isEditing ? editValue : displayValue"
    @blur="handleBlur"
    @focus="handleFocus"
    @keydown="handleKeyDown"
    @update:model-value="handleInput"
  >
    <template v-if="appendIcon" #append-icon>
      <LIconButton icon="lnx-info" @click.stop="openModal" />
    </template>
  </LTextfield>
</template>

<script lang="ts" setup>
import { LTextfield, LIconButton } from '@wgg.hyve/hyve-ui-library'
import { computed, ref } from 'vue'
import { extractNumberFromFormattedString, formatNumber } from '@/services/helper.service.ts'
import { isNullish } from '@/helpers/type-checks'

interface Props {
  fieldId: string
  clearable?: boolean
  color?: string
  disabled?: boolean
  type?: string
  inputMode?: string
  modelValue?: string | number
  error?: string
  alias?: string
  prefixValue?: string
  suffixValue?: string
  infoMessage?: string
  loading?: boolean
  prependIcon?: string
  appendIcon?: string
  modalContent?: string
  max?: number
  readOnly?: boolean
  nullable?: boolean
  mask?: Record<string, any>
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  type: 'text',
  loading: false,
  nullable: false
})

const emit = defineEmits<{
  (e: 'openModal', content: string): void
  (e: 'update:modelValue', value: number | string | null): void
}>()

const isDisabled = computed(() => !!props.disabled || !!props.readOnly)

const isEditing = ref(false)
const editValue = ref('')

const displayValue = computed(() => {
  if (typeof props.modelValue === 'number') {
    return formatNumber(props.modelValue)
  }
  return props.modelValue || ''
})

const handleInput = (value: string) => {
  editValue.value = value.replace(/[^\d,]/g, '')
}

const handleFocus = (event: FocusEvent) => {
  const input = event.target as HTMLInputElement
  isEditing.value = true

  editValue.value = props.modelValue?.toString().replace('.', ',') || ''
  input.select()
}

const handleBlur = () => {
  if (props.nullable && !editValue.value && (typeof editValue.value === 'string' || isNullish(editValue.value)))
    return emit('update:modelValue', null)

  let numValue = extractNumberFromFormattedString(editValue.value)
  isEditing.value = false

  if (!editValue.value) {
    emit('update:modelValue', 0)
    return
  }

  emit('update:modelValue', isNaN(numValue) ? 0 : numValue)
}

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === '.') {
    // if key is a dot, send a comma instead
    event.preventDefault()
    editValue.value += ','
  }
}

const openModal = () => {
  if (props.modalContent) emit('openModal', props.modalContent)
}
</script>
